// src/components/dashboard/Workshop5Charts.js
// Dashboard Atelier 5 — the risk-scenario table reuses the exact same data and
// component as "Récapitulatif des scénarios de risque" (Synthèse, Atelier 5).
// Residual figures follow the RR card display logic exactly
// (RiskScenarioListView.js:1039-1047): per attack path, the riskScenarios join
// (store getRiskScenarios()) with the card's own fallback — unset residual
// renders as initial (no-change bars). Fallback rows are NAMED in a note.
// Only rows missing even initial values render as explicit unmapped
// markers/notes, never zero.
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalysisStore from '../../stores/useAnalysisStore';
import useAtelier5Store from '../../stores/useAtelier5Store';
import {
    RiskScenarioRecapTable,
    buildAttackPathScenarios,
    buildRiskScenarioIndexMap,
    resolveRiskLevelsConfig,
} from '../Atelier5/ActiviteA/RiskScenarioListView';
import EChart from './charts/EChart';
import DashboardGrid from './DashboardGrid';
import { W5_DEFAULT_LAYOUT } from './defaultLayouts';
import {
    W5_TREATMENT_OPTIONS,
    W5_OPTION_COLORS,
    buildRiskScenarioBars,
    buildResidualMatrix,
    buildOptionDistributionByAttackPath,
    buildBudgetByCategory,
    buildTreatmentCoverage,
    resolveRiskColorHex,
    resolveRiskAcceptable,
} from './charts/workshop5Data';

const CATEGORY_FALLBACKS = {
    governance: 'Governance',
    protection: 'Protection',
    defense: 'Defense',
    resilience: 'Resilience',
    other: 'Other'
};

const Workshop5Charts = ({ data, rawStrategicScenarios = [], editMode = false, layouts = null, onLayoutChange = null, onToggleHidden = null }) => {
    const { t } = useTranslation();
    void data;
    void rawStrategicScenarios;

    // Same source as Synthèse: synthesisData from the store (not backend aggregates,
    // not atelier4 operationalScenarios).
    const synthesisData = useAtelier5Store((s) => s.synthesisData);
    const currentAnalysis = useAnalysisStore((s) => s.currentAnalysis);

    const strategicScenarios = useMemo(() => synthesisData?.strategicScenarios || [], [synthesisData]);
    const operationalScenarios = useMemo(() => synthesisData?.operationalScenarios || [], [synthesisData]);
    const riskLevelsCount = currentAnalysis?.riskLevelsCount || 5;

    const riskLevelsConfig = useMemo(
        () => resolveRiskLevelsConfig(currentAnalysis?.riskLevels, riskLevelsCount),
        [currentAnalysis?.riskLevels, riskLevelsCount]
    );
    const attackPathScenarios = useMemo(
        () => buildAttackPathScenarios(strategicScenarios, operationalScenarios, riskLevelsCount),
        [strategicScenarios, operationalScenarios, riskLevelsCount]
    );
    const riskScenarioIndexMap = useMemo(
        () => buildRiskScenarioIndexMap(strategicScenarios),
        [strategicScenarios]
    );

    // Same join as the RR cards (RiskScenarioListView): riskScenarios from the
    // store, matched per attack path (r.attackPathId === ap.id).
    const riskScenarios = useMemo(
        () => useAtelier5Store.getState().getRiskScenarios(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [synthesisData]
    );

    // Summary counts consistent with the table rows (risk scenarios, not strategic).
    const totalScenarios = attackPathScenarios.length;
    const scenariosWithTreatment = useMemo(() => {
        let count = 0;
        for (const ap of attackPathScenarios) {
            const ss = strategicScenarios.find((s) =>
                s.attackPaths?.some((a) => (typeof a === 'string' ? a : a.id) === ap.id)
            );
            if (ss?.attackPathTreatments?.[ap.id]?.option) count += 1;
        }
        return count;
    }, [attackPathScenarios, strategicScenarios]);

    // Chart datasets — comparison is keyed per risk scenario (= attack path,
    // refs R{n} shared with the recap table); both residual views read exactly
    // what the RR cards display (card-parity join with fallback to initial).
    const comparison = useMemo(
        () => buildRiskScenarioBars(attackPathScenarios, strategicScenarios, riskScenarioIndexMap, 15, riskScenarios),
        [attackPathScenarios, strategicScenarios, riskScenarioIndexMap, riskScenarios]
    );
    const residualMatrix = useMemo(
        () => buildResidualMatrix(strategicScenarios, attackPathScenarios, riskLevelsCount, riskScenarioIndexMap, riskScenarios),
        [strategicScenarios, attackPathScenarios, riskLevelsCount, riskScenarioIndexMap, riskScenarios]
    );
    const optionDistribution = useMemo(
        () => buildOptionDistributionByAttackPath(attackPathScenarios, strategicScenarios),
        [strategicScenarios, attackPathScenarios]
    );
    const budget = useMemo(
        () => buildBudgetByCategory(strategicScenarios),
        [strategicScenarios]
    );
    const coverage = useMemo(
        () => buildTreatmentCoverage(strategicScenarios, attackPathScenarios),
        [strategicScenarios, attackPathScenarios]
    );

    const categoryLabel = (category) => t(
        `dashboard.workshop5.budgetCategories.${category}`,
        CATEGORY_FALLBACKS[category] || category
    );

    const UNMAPPED_REASON_KEYS = {
        'no-initial': 'NoInitial'
    };

    const UNMAPPED_FIELD_KEYS = {
        gravity: 'Gravity',
        likelihood: 'Likelihood'
    };

    // Group unmapped details by reason code for concise one-line-per-reason notes.
    const groupUnmappedDetails = (details) => {
        const groups = new Map();
        (Array.isArray(details) ? details : []).forEach((item) => {
            const key = item?.reason || 'no-initial';
            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key).push(item);
        });
        return [...groups.entries()];
    };

    const formatUnmappedReason = (reason, items) => {
        const base = t(`dashboard.workshop5.unmappedReason${UNMAPPED_REASON_KEYS[reason] || 'NoInitial'}`, reason);
        const badFields = [...new Set(items.flatMap((item) => item?.bad || []))];
        if (badFields.length === 0) {
            return base;
        }
        const fields = badFields
            .map((field) => t(`dashboard.workshop5.unmappedField${UNMAPPED_FIELD_KEYS[field] || field}`, field))
            .join(', ');
        return `${base} (${fields})`;
    };

    const renderUnmappedDetails = (details, messageKey, fallback) => groupUnmappedDetails(details).map(([reason, items]) => (
        <p key={reason} className="text-xs text-gray-500 mt-1">
            {t(messageKey, fallback, {
                refs: items.map((item) => item.shortRef).join(', '),
                reason: formatUnmappedReason(reason, items)
            })}
        </p>
    ));

    const bands = useMemo(() => {
        const levels = Array.isArray(riskLevelsConfig) ? riskLevelsConfig : [];
        const fmt = (lv) => `${lv.minScore}-${lv.maxScore}`;
        return {
            acceptable: levels.filter((lv) => lv.acceptable !== false).map(fmt).join(', '),
            unacceptable: levels.filter((lv) => lv.acceptable === false).map(fmt).join(', ')
        };
    }, [riskLevelsConfig]);

    // 2.1 Initial vs residual grouped bars, one row per risk scenario
    // (attack path, R{n} refs). Top 15 by initial, sorted by residual desc.
    const comparisonOption = useMemo(() => {
        const rows = comparison;
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const row = rows[params?.[0]?.dataIndex];
                    if (!row) {
                        return '';
                    }
                    const lines = [row.name];
                    lines.push(`${t('dashboard.workshop5.comparisonInitial', 'Initial')}: G${row.initialGravity} x V${row.initialLikelihood} = ${row.initialScore}`);
                    lines.push(`${t('dashboard.workshop5.comparisonResidual', 'Residual')}: G${row.residualGravity} x V${row.residualLikelihood} = ${row.residualScore}`);
                    return lines.join('<br />');
                }
            },
            legend: { bottom: 0 },
            grid: { left: 8, right: 16, top: 24, bottom: 64, containLabel: true },
            xAxis: {
                type: 'category',
                data: rows.map((row) => row.shortLabel),
                axisLabel: { interval: 0, rotate: 30 }
            },
            yAxis: { type: 'value' },
            series: [
                {
                    name: t('dashboard.workshop5.comparisonInitial', 'Initial'),
                    type: 'bar',
                    data: rows.map((row) => ({
                        value: row.initialScore,
                        itemStyle: { color: resolveRiskColorHex(row.initialScore, riskLevelsConfig) }
                    }))
                },
                {
                    name: t('dashboard.workshop5.comparisonResidual', 'Residual'),
                    type: 'bar',
                    data: rows.map((row) => ({
                        value: row.residualScore,
                        itemStyle: { color: resolveRiskColorHex(row.residualScore, riskLevelsConfig) }
                    }))
                }
            ]
        };
    }, [comparison, riskLevelsConfig, t]);

    // 2.2 Option donut (fixed 4-slice enum).
    const optionDonutOption = useMemo(() => ({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0 },
        title: {
            text: String(optionDistribution.total),
            subtext: `${coverage.percentage}%`,
            left: 'center',
            top: '32%',
            textStyle: { fontSize: 26, fontWeight: 700, color: '#111827' },
            subtextStyle: { fontSize: 14, color: '#6B7280' }
        },
        series: [{
            type: 'pie',
            radius: ['50%', '70%'],
            center: ['50%', '45%'],
            label: { show: false },
            data: W5_TREATMENT_OPTIONS.map((option) => ({
                name: t(`dashboard.workshop5.options.${option}`, option),
                value: optionDistribution[option],
                itemStyle: { color: W5_OPTION_COLORS[option] }
            }))
        }]
    }), [optionDistribution, coverage.percentage, t]);

    // 2.3 Residual matrix — same axes/cell-sizing as the W4 matrix.
    const residualMatrixOption = useMemo(() => ({
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                const likelihood = params.value[0] + 1;
                const gravity = params.value[1] + 1;
                const score = gravity * likelihood;
                const acceptable = resolveRiskAcceptable(score, riskLevelsConfig);
                const base = `G${gravity} x V${likelihood}: ${params.value[2]}`;
                if (acceptable === null) {
                    return base;
                }
                return acceptable
                    ? `${base}<br />${t('dashboard.workshop5.matrixAcceptable', 'Acceptable')}`
                    : `${base}<br />${t('dashboard.workshop5.matrixUnacceptable', 'Unacceptable')}`;
            }
        },
        grid: { left: 8, right: 16, top: 8, bottom: 64, containLabel: true },
        xAxis: {
            type: 'category',
            data: residualMatrix.xLabels,
            name: t('dashboard.workshop5.likelihood', 'Likelihood'),
            nameLocation: 'middle',
            nameGap: 28
        },
        yAxis: {
            type: 'category',
            data: residualMatrix.yLabels,
            name: t('dashboard.workshop5.gravity', 'Gravity')
        },
        visualMap: {
            min: 0,
            max: Math.max(residualMatrix.max, 1),
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: 0,
            inRange: { color: ['#ECFDF5', '#F59E0B', '#DC2626'] }
        },
        series: [{
            type: 'heatmap',
            data: residualMatrix.data,
            label: { show: true },
            emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
        }]
    }), [residualMatrix, riskLevelsConfig, t]);

    // 2.4 Budget by category, sorted desc — overdue bars get the dashed dark
    // outline marker (same principle as W4 defaulted cells).
    const budgetOption = useMemo(() => {
        const rows = budget.rows;
        return {
            tooltip: {
                trigger: 'item',
                formatter: (params) => {
                    const row = rows[params.dataIndex];
                    if (!row) {
                        return '';
                    }
                    const lines = [`${categoryLabel(row.category)}: ${row.total} €`];
                    if (row.overdueCount > 0) {
                        lines.push(t('dashboard.workshop5.budgetOverdueTooltip', 'Overdue measures: {{count}}', { count: row.overdueCount }));
                    }
                    return lines.join('<br />');
                }
            },
            grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
            xAxis: { type: 'value', name: '€' },
            yAxis: {
                type: 'category',
                data: rows.map((row) => categoryLabel(row.category)),
                axisLabel: { interval: 0 }
            },
            series: [{
                type: 'bar',
                data: rows.map((row) => (row.overdue ? {
                    value: row.total,
                    itemStyle: { borderColor: '#1F2937', borderWidth: 2, borderType: 'dashed' }
                } : row.total)),
                label: { show: true, position: 'right', formatter: '{c} €' },
                emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0, 0, 0, 0.3)' } }
            }]
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budget, t]);

    if (!synthesisData) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <p className="text-gray-500">{t('workshop5Charts.noData')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Enhanced Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="group bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-blue-800 mb-1">{t('workshop5Charts.riskScenarios', 'Scénarios de risques')}</h4>
                            <p className="text-3xl font-bold text-blue-900 group-hover:text-blue-700 transition-colors">
                                {totalScenarios}
                            </p>
                            <p className="text-xs text-blue-600 mt-1">{t('workshop5Charts.scenariosAnalyzed')}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:bg-blue-600 transition-colors shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="group bg-gradient-to-br from-purple-50 to-violet-100 p-6 rounded-xl border border-purple-200 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-purple-800 mb-1">{t('workshop5Charts.withTreatment')}</h4>
                            <p className="text-3xl font-bold text-purple-900 group-hover:text-purple-700 transition-colors">
                                {scenariosWithTreatment}
                            </p>
                            <p className="text-xs text-purple-600 mt-1">
                                {totalScenarios > 0 ? t('workshop5Charts.treatedPercentage', { percentage: ((scenariosWithTreatment / totalScenarios) * 100).toFixed(1) }) : t('workshop5Charts.treatedPercentageZero')}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center group-hover:bg-purple-600 transition-colors shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="group bg-gradient-to-br from-amber-50 to-orange-100 p-6 rounded-xl border border-amber-200 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-medium text-amber-800 mb-1">{t('dashboard.workshop5.kpiOverdue', 'Overdue measures')}</h4>
                            <p className="text-3xl font-bold text-amber-900 group-hover:text-amber-700 transition-colors">
                                {budget.overdueCount}
                            </p>
                            <p className="text-xs text-amber-600 mt-1">
                                {t('dashboard.workshop5.kpiOverdueSub', 'Pending budget: {{amount}} €', { amount: budget.totalBudget })}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center group-hover:bg-amber-600 transition-colors shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts (draggable/resizable in edit mode) */}
            <DashboardGrid
              items={[
                {
                  id: 'w5-comparison',
                  fullscreenTitle: t('dashboard.workshop5.comparisonTitle', 'Initial vs residual risk'),
                  fullscreenContent: (<EChart option={comparisonOption} height={560} />),
                  element: (
                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-lg h-full flex flex-col">
                      <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.workshop5.comparisonTitle', 'Initial vs residual risk')}</h3>
                      {comparison.total > 0 ? (
                        <div className="flex-1 min-h-0" style={{ minHeight: 100 }}><EChart option={comparisonOption} height="100%" /></div>
                      ) : (
                        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.workshop5.comparisonEmpty', 'No risk scenarios with initial values')}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {t('dashboard.workshop5.comparisonCaption', 'Top {{shown}} of {{total}} risk scenarios by initial risk', { shown: comparison.length, total: comparison.total })}
                      </p>
                      {comparison.unmappedCount > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('dashboard.workshop5.comparisonUnmapped', '{{count}} risk scenarios without initial values (unmapped)', { count: comparison.unmappedCount })}
                        </p>
                      )}
                      {renderUnmappedDetails(comparison.unmappedDetails, 'dashboard.workshop5.comparisonUnmappedDetails', 'No usable initial values: {{refs}} ({{reason}})')}
                      {comparison.fallbackRefs?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('dashboard.workshop5.fallbackNote', 'Residual not set — showing initial: {{refs}}', { refs: comparison.fallbackRefs.join(', ') })}
                        </p>
                      )}
                      <a href="#w5-recap-table" className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline mt-1 inline-block">
                        {t('dashboard.workshop5.comparisonLink', 'See the full recap table')}
                      </a>
                    </div>
                  )
                },
                {
                  id: 'w5-options',
                  fullscreenTitle: t('dashboard.workshop5.optionTitle', 'Treatment options'),
                  fullscreenContent: (<EChart option={optionDonutOption} height={560} />),
                  element: (
                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-lg h-full flex flex-col">
                      <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.workshop5.optionTitle', 'Treatment options')}</h3>
                      {optionDistribution.total > 0 ? (
                        <div className="flex-1 min-h-0" style={{ minHeight: 90 }}><EChart option={optionDonutOption} height="100%" /></div>
                      ) : (
                        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.workshop5.optionEmpty', 'No treatment options recorded')}</p>
                      )}
                      {optionDistribution.untreated > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('dashboard.workshop5.optionUntreatedNote', '{{count}} scenarios without a treatment option', { count: optionDistribution.untreated })}
                        </p>
                      )}
                      {optionDistribution.other > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('dashboard.workshop5.optionOtherNote', '{{count}} scenarios with an option outside the 4-option enum', { count: optionDistribution.other })}
                        </p>
                      )}
                    </div>
                  )
                },
                {
                  id: 'w5-residualMatrix',
                  fullscreenTitle: t('dashboard.workshop5.matrixTitle', 'Residual risk matrix'),
                  fullscreenContent: (<EChart option={residualMatrixOption} height={560} />),
                  element: (
                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-lg h-full flex flex-col">
                      <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.workshop5.matrixTitle', 'Residual risk matrix')}</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('dashboard.workshop5.matrixBands', 'Acceptable: {{acceptable}} — Unacceptable: {{unacceptable}}', { acceptable: bands.acceptable, unacceptable: bands.unacceptable })}
                      </p>
                      {residualMatrix.total > 0 ? (
                        <div className="flex-1 min-h-0" style={{ minHeight: 136 }}><EChart option={residualMatrixOption} height="100%" /></div>
                      ) : (
                        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.workshop5.matrixEmpty', 'No risk scenarios with initial values')}</p>
                      )}
                      {residualMatrix.unmappedCount > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('dashboard.workshop5.matrixUnmapped', '{{count}} risk scenarios without initial values (unmapped) — excluded, not zero', { count: residualMatrix.unmappedCount })}
                        </p>
                      )}
                      {renderUnmappedDetails(residualMatrix.unmappedDetails, 'dashboard.workshop5.matrixUnmappedDetails', 'No usable initial values: {{refs}} ({{reason}})')}
                      {residualMatrix.fallbackRefs?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('dashboard.workshop5.fallbackNote', 'Residual not set — showing initial: {{refs}}', { refs: residualMatrix.fallbackRefs.join(', ') })}
                        </p>
                      )}
                    </div>
                  )
                },
                {
                  id: 'w5-budget',
                  fullscreenTitle: t('dashboard.workshop5.budgetTitle', 'Pending budget by category'),
                  fullscreenContent: (<EChart option={budgetOption} height={560} />),
                  element: (
                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-lg h-full flex flex-col">
                      <h3 className="font-semibold text-gray-800 mb-1">{t('dashboard.workshop5.budgetTitle', 'Pending budget by category')}</h3>
                      {budget.rows.length > 0 ? (
                        <div className="flex-1 min-h-0" style={{ minHeight: Math.max(90, budget.rows.length * 24) }}><EChart option={budgetOption} height="100%" /></div>
                      ) : (
                        <p className="text-sm text-gray-400 py-8 text-center">{t('dashboard.workshop5.budgetEmpty', 'No pending budgeted measures')}</p>
                      )}
                      {budget.overdueCount > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('dashboard.workshop5.budgetOverdueNote', '{{count}} overdue measures (dashed outline)', { count: budget.overdueCount })}
                        </p>
                      )}
                    </div>
                  )
                }
              ]}
              defaultLayout={W5_DEFAULT_LAYOUT}
              editMode={editMode}
              layouts={layouts}
              onLayoutChange={onLayoutChange}
              onToggleHidden={onToggleHidden}
            />

            {/* Risk scenarios — same rows as Récapitulatif des scénarios de risque */}
            <div id="w5-recap-table" className="scroll-mt-4">
                {attackPathScenarios.length > 0 ? (
                    <RiskScenarioRecapTable
                        strategicScenarios={strategicScenarios}
                        attackPathScenarios={attackPathScenarios}
                        riskScenarioIndexMap={riskScenarioIndexMap}
                        riskLevels={riskLevelsConfig}
                        riskScenarios={riskScenarios}
                    />
                ) : (
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                        <div className="text-center py-12">
                            <div className="text-gray-400 mb-4">
                                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                </svg>
                            </div>
                            <p className="text-gray-500 text-lg">{t('workshop5Charts.noScenarioData')}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Workshop5Charts;
